import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  IconBug,
  IconDots,
  IconDotsVertical,
  IconPencil,
  IconShare2,
  IconShareOff,
  IconTrash,
} from "@tabler/icons-react";
import BottomPanel from "../app/panels/BottomPanel";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { PopoverCard, usePopoverAnchor } from "../ui/Popover";
import { DesktopMenu, DesktopMenuItem } from "../ui/DesktopMenu";
import { DesktopConfirmDialog } from "../app/DesktopConfirmDialog";
import { DesktopDialogOverlay } from "../app/DesktopDialogOverlay";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { cn } from "../../lib/utils";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";

/** Which of the menu's four faces is on screen. */
type MenuView = "closed" | "menu" | "rename" | "delete";

/**
 * The owner's actions on one conversation — Share, Stop sharing, Rename,
 * Delete — behind a
 * 3-dots trigger. One component for the three surfaces that offer them: a row of
 * the history drawer, the mobile conversation header, and the desktop sticky
 * header.
 *
 * The machinery lives elsewhere: `usePopoverAnchor` owns the refs, the measuring
 * and the keyboard, `useSingleFlightAction` owns the write in flight. What is
 * left here is ephemeral view state — which face is showing — the draft of the
 * rename field, which React Hook Form holds, plus the copy and the three
 * callbacks.
 *
 * Both confirmations stay open until their caller reports the write landed: a
 * failed rename must not throw away what the user typed.
 */
export function ConversationMenu({
  title,
  onShare,
  onUnshare,
  onDebug,
  onRename,
  onDelete,
  className,
}: {
  /** Prefills the rename field. Null on a conversation the agent has not named yet. */
  title: string | null;
  /** Null hides the Share item (the desktop header carries its own Share button). */
  onShare: (() => void) | null;
  /** Null hides the Stop sharing item: the conversation is not shared, or sharing is off. */
  onUnshare: (() => void) | null;
  /** Null hides the Debug item: only the assistant engine's debug flag opens its drawer. */
  onDebug: (() => void) | null;
  /** Resolves true once the new title is persisted. */
  onRename: (title: string) => Promise<boolean>;
  /** Resolves true once the conversation is gone. */
  onDelete: () => Promise<boolean>;
  /** Wrapper classes — the drawer row uses them for its hover reveal. */
  className: string | null;
}): React.ReactElement {
  const { t } = useTranslation();
  const [view, setView] = useState<MenuView>("closed");
  // RHF holds the draft of the rename field (form state, not view state).
  const { watch, setValue } = useForm<{ draft: string }>({
    defaultValues: { draft: "" },
  });
  const draft = watch("draft");
  // The two sheets are `SwipeableDrawer`s, and MUI attaches document-level touch
  // listeners (one of them non-passive) for every one that is MOUNTED, open or
  // not. The history drawer renders a menu per row, so mounting them eagerly
  // would cost a hundred listeners fighting the scroll of a list whose rows are
  // mostly never touched. They arrive with the first tap on the trigger instead,
  // which also keeps their open AND close animations — rendering a sheet only
  // while it is open would cut the exit transition off.
  const [everOpened, setEverOpened] = useState(false);
  const anchor = usePopoverAnchor({
    isOpen: view === "menu",
    onDismiss: () => {
      setView("closed");
    },
    edge: "right",
  });
  // One mutation at a time across the two sheets: the rename and the delete are
  // both terminal actions on the same conversation.
  const action = useSingleFlightAction();
  const isRunning = action.state.status === "running";

  const currentTitle = title === null ? "" : title;
  const nextTitle = draft.trim();
  const canSave = nextTitle !== "" && nextTitle !== currentTitle && !isRunning;

  function submitRename(): void {
    void action.run(async () => {
      const done = await onRename(nextTitle);
      if (done) setView("closed");
    });
  }

  function confirmDelete(): void {
    void action.run(async () => {
      const done = await onDelete();
      if (done) setView("closed");
    });
  }

  function openRename(): void {
    setValue("draft", currentTitle);
    setView("rename");
  }

  function openDelete(): void {
    setView("delete");
  }

  // A write in flight owns the sheet: closing it would discard the draft the
  // single-flight contract exists to protect, and the toast that follows would
  // tell the user to retype what they had already typed.
  function dismissSheet(): void {
    if (isRunning) return;
    setView("closed");
  }

  const itemClass =
    "flex min-h-11 w-full cursor-pointer items-center gap-2.5 border-none bg-transparent px-4 text-left font-dm-sans text-ui-body font-medium hover:bg-ds-black/4";

  return (
    <div
      // `data-open` survives the popover: a drawer row reveals its trigger on
      // hover, and the pointer leaves the row the moment the menu opens over it.
      data-open={view === "closed" ? "false" : "true"}
      className={cn("relative flex items-center", className)}
    >
      <button
        ref={anchor.triggerRef}
        type="button"
        aria-label={t("More")}
        aria-haspopup="menu"
        aria-expanded={view === "menu"}
        className={cn(
          "flex shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-ds-black hover:bg-ds-black/6",
          // The desktop row's trigger (Figma Recent): a 16px vertical glyph in
          // a 24px target.
          isDesktopShell() ? "size-6 rounded-md" : "size-8 rounded-lg"
        )}
        onClick={() => {
          setEverOpened(true);
          setView(view === "menu" ? "closed" : "menu");
        }}
      >
        {isDesktopShell() ? (
          <IconDotsVertical className="size-4" stroke={1.25} />
        ) : (
          <IconDots className="size-5" stroke={1.5} />
        )}
      </button>

      {view === "menu" &&
        // The desktop app draws every dropdown as its one menu; the phone
        // keeps rows tall enough for a thumb.
        (isDesktopShell() ? (
          <DesktopMenu anchor={anchor} className="w-48.5">
            {onShare !== null && (
              <DesktopMenuItem
                description={null}
                icon={IconShare2}
                label={t("Share")}
                trailing={null}
                tone="default"
                action={{
                  kind: "button",
                  onClick: () => {
                    setView("closed");
                    onShare();
                  },
                }}
              />
            )}
            {onUnshare !== null && (
              <DesktopMenuItem
                description={null}
                icon={IconShareOff}
                label={t("Stop sharing")}
                trailing={null}
                tone="default"
                action={{
                  kind: "button",
                  onClick: () => {
                    setView("closed");
                    onUnshare();
                  },
                }}
              />
            )}
            <DesktopMenuItem
              description={null}
              icon={IconPencil}
              label={t("Rename")}
              trailing={null}
              tone="default"
              action={{ kind: "button", onClick: openRename }}
            />
            {onDebug !== null && (
              <DesktopMenuItem
                description={null}
                icon={IconBug}
                label="Debug"
                trailing={null}
                tone="default"
                action={{
                  kind: "button",
                  onClick: () => {
                    setView("closed");
                    onDebug();
                  },
                }}
              />
            )}
            <DesktopMenuItem
              description={null}
              icon={IconTrash}
              label={t("Delete")}
              trailing={null}
              tone="danger"
              action={{ kind: "button", onClick: openDelete }}
            />
          </DesktopMenu>
        ) : (
          <PopoverCard anchor={anchor} role="menu" className="w-52">
            {onShare !== null && (
              <button
                type="button"
                role="menuitem"
                className={cn(itemClass, "text-ds-black")}
                onClick={() => {
                  setView("closed");
                  onShare();
                }}
              >
                <IconShare2 className="size-4 shrink-0" stroke={1.5} />
                {t("Share")}
              </button>
            )}
            {onUnshare !== null && (
              <button
                type="button"
                role="menuitem"
                className={cn(itemClass, "text-ds-black")}
                onClick={() => {
                  setView("closed");
                  onUnshare();
                }}
              >
                <IconShareOff className="size-4 shrink-0" stroke={1.5} />
                {t("Stop sharing")}
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className={cn(itemClass, "text-ds-black")}
              onClick={openRename}
            >
              <IconPencil className="size-4 shrink-0" stroke={1.5} />
              {t("Rename")}
            </button>
            {onDebug !== null && (
              <button
                type="button"
                role="menuitem"
                className={cn(itemClass, "text-ds-black")}
                onClick={() => {
                  setView("closed");
                  onDebug();
                }}
              >
                <IconBug className="size-4 shrink-0" stroke={1.5} />
                Debug
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className={cn(itemClass, "text-secondary-raspberry-1")}
              onClick={openDelete}
            >
              <IconTrash className="size-4 shrink-0" stroke={1.5} />
              {t("Delete")}
            </button>
          </PopoverCard>
        ))}

      {/* The desktop asks in its dialogs (Figma "Dialog / Confirm", "Dialog ·
          Rename"), the phone in its sheets. */}
      {view === "rename" && isDesktopShell() ? (
        <DesktopDialogOverlay
          label={t("Rename conversation")}
          onClose={dismissSheet}
          cardClassName="flex w-full max-w-120 flex-col gap-4 rounded-2xl bg-ds-white p-6 shadow-desktop-modal"
        >
          <h2 className="m-0 font-dm-sans text-heading-xs font-semibold text-ds-slate-950">
            {t("Rename conversation")}
          </h2>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              submitRename();
            }}
          >
            <input
              autoFocus
              maxLength={200}
              value={draft}
              placeholder={t("Conversation name")}
              aria-label={t("Conversation name")}
              onChange={(event) => {
                setValue("draft", event.target.value);
              }}
              // The DS Input at its desktop size (Figma "Input" md): focus
              // darkens the border, never a glow.
              className="h-10 w-full rounded-xl border border-solid border-ds-slate-200 bg-ds-white px-4 font-dm-sans text-ui-body text-ds-slate-950 outline-none placeholder:text-ds-slate-500 focus:border-ds-slate-400"
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isRunning}
                onClick={dismissSheet}
              >
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!canSave}
              >
                {t("Save")}
              </Button>
            </div>
          </form>
        </DesktopDialogOverlay>
      ) : null}
      {view === "delete" && isDesktopShell() ? (
        <DesktopConfirmDialog
          title={t("Delete conversation?")}
          confirmLabel={t("Delete")}
          note={t("This can't be undone.")}
          disabled={isRunning}
          confirmDisabled={false}
          onConfirm={confirmDelete}
          onCancel={dismissSheet}
        >
          <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-700">
            {t("This will permanently delete this conversation:")}
          </p>
          {title !== null && (
            <p className="m-0 font-dm-sans text-sm font-semibold text-ds-slate-950">
              {title}
            </p>
          )}
        </DesktopConfirmDialog>
      ) : null}
      {everOpened && !isDesktopShell() && (
        <>
          <BottomPanel
            isOpen={view === "rename"}
            onClose={dismissSheet}
            title={t("Rename conversation")}
            displayCloseButton={!isRunning}
          >
            <form
              className="flex flex-col gap-4 p-4"
              onSubmit={(event) => {
                event.preventDefault();
                submitRename();
              }}
            >
              <Input
                autoFocus
                maxLength={200}
                value={draft}
                placeholder={t("Conversation name")}
                aria-label={t("Conversation name")}
                onChange={(event) => {
                  setValue("draft", event.target.value);
                }}
              />
              <div className="flex flex-col gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={!canSave}
                >
                  {t("Save")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  disabled={isRunning}
                  onClick={dismissSheet}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </form>
          </BottomPanel>

          <BottomPanel
            isOpen={view === "delete"}
            onClose={dismissSheet}
            title={t("Delete conversation?")}
            displayCloseButton={!isRunning}
          >
            <div className="flex flex-col gap-4 p-4">
              <p className="m-0 font-dm-sans text-ui-body font-normal text-ds-slate-700">
                {t(
                  "This conversation and its attached files will be permanently deleted. Its share link, if any, will stop working."
                )}
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  size="md"
                  disabled={isRunning}
                  onClick={confirmDelete}
                >
                  {t("Delete")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  disabled={isRunning}
                  onClick={dismissSheet}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </div>
          </BottomPanel>
        </>
      )}
    </div>
  );
}
